/**
 * Record Reminders Settings Component
 * Configure automated reminders for expiring children's records
 */
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import axios from "axios";
import { 
  Bell, Mail, MessageSquare, Plus, X, Send, Clock, 
  AlertTriangle, CheckCircle, Settings, Eye, History
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Preset interval options
const PRESET_INTERVALS = [
  { days: 60, label: "60 days" },
  { days: 45, label: "45 days" },
  { days: 30, label: "30 days" },
  { days: 21, label: "21 days" },
  { days: 14, label: "14 days" },
  { days: 7, label: "7 days" },
  { days: 3, label: "3 days" },
  { days: 1, label: "1 day" },
];

export const RecordRemindersSettings = () => {
  const [settings, setSettings] = useState({
    enabled: true,
    notificationType: "email",
    intervals: [
      { days: 30, enabled: true },
      { days: 14, enabled: true },
      { days: 7, enabled: true }
    ],
    customMessage: "",
    includeFormsList: true
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [customDays, setCustomDays] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [preview, setPreview] = useState({ children: [], totalReminders: 0 });
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [sending, setSending] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testPhone, setTestPhone] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/record-reminders/settings`);
      setSettings(res.data);
    } catch (error) {
      console.error("Error fetching reminder settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/record-reminders/settings`, settings);
      toast.success("Reminder settings saved");
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const toggleInterval = (days, enabled) => {
    setSettings(prev => ({
      ...prev,
      intervals: prev.intervals.map(i => 
        i.days === days ? { ...i, enabled } : i
      )
    }));
  };

  const addInterval = async () => {
    const days = parseInt(customDays);
    if (isNaN(days) || days < 1 || days > 365) {
      toast.error("Please enter a valid number of days (1-365)");
      return;
    }

    if (settings.intervals.some(i => i.days === days)) {
      toast.error(`${days}-day interval already exists`);
      return;
    }

    try {
      const res = await axios.post(`${API}/record-reminders/add-interval?days=${days}`);
      setSettings(prev => ({
        ...prev,
        intervals: res.data.intervals
      }));
      setCustomDays("");
      toast.success(`Added ${days}-day reminder`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add interval");
    }
  };

  const removeInterval = async (days) => {
    try {
      const res = await axios.delete(`${API}/record-reminders/remove-interval/${days}`);
      setSettings(prev => ({
        ...prev,
        intervals: res.data.intervals
      }));
      toast.success(`Removed ${days}-day reminder`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove interval");
    }
  };

  const addPresetInterval = (days) => {
    if (settings.intervals.some(i => i.days === days)) {
      toast.error(`${days}-day interval already exists`);
      return;
    }
    setCustomDays(days.toString());
  };

  const fetchPreview = async () => {
    setLoadingPreview(true);
    try {
      const res = await axios.get(`${API}/record-reminders/preview`);
      setPreview(res.data);
      setShowPreview(true);
    } catch (error) {
      console.error("Error fetching preview:", error);
      toast.error("Failed to load preview");
    } finally {
      setLoadingPreview(false);
    }
  };

  const sendRemindersNow = async () => {
    setShowSendConfirm(false);
    setSending(true);
    try {
      const res = await axios.post(`${API}/record-reminders/send-now`);
      toast.success(`Sent ${res.data.sent} reminders to ${res.data.childrenNotified} families`);
      if (res.data.failed > 0) {
        toast.error(`${res.data.failed} reminders failed to send`);
      }
      fetchHistory();
    } catch (error) {
      console.error("Error sending reminders:", error);
      toast.error(error.response?.data?.detail || "Failed to send reminders");
    } finally {
      setSending(false);
    }
  };

  const sendTestReminder = async () => {
    if (settings.notificationType === "email" && !testEmail) {
      toast.error("Please enter a test email address");
      return;
    }
    if (settings.notificationType === "sms" && !testPhone) {
      toast.error("Please enter a test phone number");
      return;
    }
    if (settings.notificationType === "both" && !testEmail && !testPhone) {
      toast.error("Please enter a test email or phone number");
      return;
    }

    setSendingTest(true);
    try {
      const res = await axios.post(`${API}/record-reminders/send-test`, {
        notificationType: settings.notificationType,
        testEmail: testEmail || null,
        testPhone: testPhone || null
      });
      
      const results = res.data.results;
      if (results.email === "sent") toast.success("Test email sent!");
      if (results.sms === "sent") toast.success("Test SMS sent!");
      if (results.email?.startsWith("failed")) toast.error(`Email failed: ${results.email}`);
      if (results.sms?.startsWith("failed")) toast.error(`SMS failed: ${results.sms}`);
    } catch (error) {
      toast.error("Failed to send test reminder");
    } finally {
      setSendingTest(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${API}/record-reminders/history`);
      setHistory(res.data.history);
      setShowHistory(true);
    } catch (error) {
      console.error("Error fetching history:", error);
    }
  };

  if (loading) {
    return (
      <Card className="border-teal-200">
        <CardContent className="p-8 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full mx-auto"></div>
          <p className="mt-4 text-gray-500">Loading reminder settings...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-teal-200">
      <CardHeader className="bg-gradient-to-r from-teal-50 to-cyan-50 border-b border-teal-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Bell className="w-6 h-6 text-teal-600" />
            <div>
              <CardTitle className="text-teal-800">Automated Record Reminders</CardTitle>
              <CardDescription>
                Send automatic reminders when children's records are coming due
              </CardDescription>
            </div>
          </div>
          <Switch
            checked={settings.enabled}
            onCheckedChange={(enabled) => setSettings(prev => ({ ...prev, enabled }))}
          />
        </div>
      </CardHeader>

      <CardContent className="p-6 space-y-6">
        {/* Notification Type */}
        <div className="space-y-3">
          <Label className="text-teal-700 font-semibold">Notification Method</Label>
          <div className="flex gap-4">
            <label className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
              settings.notificationType === 'email' ? 'border-teal-500 bg-teal-50' : 'border-gray-200 hover:border-teal-300'
            }`}>
              <input
                type="radio"
                name="notificationType"
                value="email"
                checked={settings.notificationType === 'email'}
                onChange={(e) => setSettings(prev => ({ ...prev, notificationType: e.target.value }))}
                className="sr-only"
              />
              <Mail className={`w-5 h-5 ${settings.notificationType === 'email' ? 'text-teal-600' : 'text-gray-400'}`} />
              <span className={settings.notificationType === 'email' ? 'text-teal-700 font-medium' : 'text-gray-600'}>
                Email Only
              </span>
            </label>

            <label className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
              settings.notificationType === 'sms' ? 'border-teal-500 bg-teal-50' : 'border-gray-200 hover:border-teal-300'
            }`}>
              <input
                type="radio"
                name="notificationType"
                value="sms"
                checked={settings.notificationType === 'sms'}
                onChange={(e) => setSettings(prev => ({ ...prev, notificationType: e.target.value }))}
                className="sr-only"
              />
              <MessageSquare className={`w-5 h-5 ${settings.notificationType === 'sms' ? 'text-teal-600' : 'text-gray-400'}`} />
              <span className={settings.notificationType === 'sms' ? 'text-teal-700 font-medium' : 'text-gray-600'}>
                SMS Only
              </span>
            </label>

            <label className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
              settings.notificationType === 'both' ? 'border-teal-500 bg-teal-50' : 'border-gray-200 hover:border-teal-300'
            }`}>
              <input
                type="radio"
                name="notificationType"
                value="both"
                checked={settings.notificationType === 'both'}
                onChange={(e) => setSettings(prev => ({ ...prev, notificationType: e.target.value }))}
                className="sr-only"
              />
              <div className="flex">
                <Mail className={`w-4 h-4 ${settings.notificationType === 'both' ? 'text-teal-600' : 'text-gray-400'}`} />
                <MessageSquare className={`w-4 h-4 -ml-1 ${settings.notificationType === 'both' ? 'text-teal-600' : 'text-gray-400'}`} />
              </div>
              <span className={settings.notificationType === 'both' ? 'text-teal-700 font-medium' : 'text-gray-600'}>
                Both
              </span>
            </label>
          </div>
          
          {(settings.notificationType === 'sms' || settings.notificationType === 'both') && (
            <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
              SMS reminders will include opt-out message for Twilio A2P compliance: "Reply STOP to opt out."
            </p>
          )}
        </div>

        {/* Reminder Intervals */}
        <div className="space-y-3">
          <Label className="text-teal-700 font-semibold">Reminder Schedule</Label>
          <p className="text-sm text-gray-500">Select when to send reminders before records are due</p>
          
          <div className="flex flex-wrap gap-2">
            {settings.intervals
              .sort((a, b) => b.days - a.days)
              .map((interval) => (
                <div
                  key={interval.days}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
                    interval.enabled 
                      ? 'bg-teal-50 border-teal-300' 
                      : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <Checkbox
                    checked={interval.enabled}
                    onCheckedChange={(checked) => toggleInterval(interval.days, checked)}
                  />
                  <span className={interval.enabled ? 'text-teal-700 font-medium' : 'text-gray-500'}>
                    {interval.days} day{interval.days !== 1 ? 's' : ''} before
                  </span>
                  <button
                    onClick={() => removeInterval(interval.days)}
                    className="text-gray-400 hover:text-red-500 ml-1"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
          </div>

          {/* Add Custom Interval */}
          <div className="flex gap-2 items-end mt-4">
            <div className="flex-1">
              <Label className="text-sm text-gray-600">Add Custom Interval</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  type="number"
                  min="1"
                  max="365"
                  value={customDays}
                  onChange={(e) => setCustomDays(e.target.value)}
                  placeholder="Days"
                  className="w-24"
                />
                <Button onClick={addInterval} size="sm" variant="outline" className="border-teal-300">
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </Button>
              </div>
            </div>
            
            <div className="flex-1">
              <Label className="text-sm text-gray-600">Quick Add Presets</Label>
              <div className="flex flex-wrap gap-1 mt-1">
                {PRESET_INTERVALS.filter(p => !settings.intervals.some(i => i.days === p.days)).slice(0, 4).map(preset => (
                  <Button
                    key={preset.days}
                    size="sm"
                    variant="ghost"
                    className="text-xs h-7 px-2"
                    onClick={() => addPresetInterval(preset.days)}
                  >
                    +{preset.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Custom Message */}
        <div className="space-y-2">
          <Label className="text-teal-700 font-semibold">Custom Message (Optional)</Label>
          <Textarea
            value={settings.customMessage || ""}
            onChange={(e) => setSettings(prev => ({ ...prev, customMessage: e.target.value }))}
            placeholder="Add a custom message to include in all reminders..."
            className="border-teal-200 min-h-[80px]"
          />
          <p className="text-xs text-gray-500">This message will appear in both email and SMS reminders</p>
        </div>

        {/* Test Reminder */}
        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
          <Label className="text-gray-700 font-semibold">Send Test Reminder</Label>
          <div className="flex gap-4">
            {(settings.notificationType === 'email' || settings.notificationType === 'both') && (
              <div className="flex-1">
                <Label className="text-xs text-gray-500">Test Email</Label>
                <Input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="email@example.com"
                  className="mt-1"
                />
              </div>
            )}
            {(settings.notificationType === 'sms' || settings.notificationType === 'both') && (
              <div className="flex-1">
                <Label className="text-xs text-gray-500">Test Phone</Label>
                <Input
                  type="tel"
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                  placeholder="(555) 555-5555"
                  className="mt-1"
                />
              </div>
            )}
          </div>
          <Button
            onClick={sendTestReminder}
            disabled={sendingTest}
            variant="outline"
            size="sm"
            className="border-gray-300"
          >
            {sendingTest ? "Sending..." : "Send Test"}
          </Button>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between pt-4 border-t">
          <div className="flex gap-2">
            <Button
              onClick={fetchPreview}
              disabled={loadingPreview}
              variant="outline"
              className="border-teal-300 text-teal-700"
            >
              <Eye className="w-4 h-4 mr-2" />
              {loadingPreview ? "Loading..." : "Preview Due Records"}
            </Button>
            <Button
              onClick={fetchHistory}
              variant="outline"
              className="border-gray-300 text-gray-700"
            >
              <History className="w-4 h-4 mr-2" />
              History
            </Button>
          </div>
          
          <div className="flex gap-2">
            <Button
              onClick={saveSettings}
              disabled={saving}
              className="bg-teal-600 hover:bg-teal-700 text-white"
            >
              <Settings className="w-4 h-4 mr-2" />
              {saving ? "Saving..." : "Save Settings"}
            </Button>
            <Button
              onClick={() => {
                fetchPreview();
                setShowSendConfirm(true);
              }}
              disabled={sending || !settings.enabled}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              <Send className="w-4 h-4 mr-2" />
              Send Reminders Now
            </Button>
          </div>
        </div>

        {/* Preview Modal */}
        {showPreview && (
          <div className="mt-4 border border-teal-200 rounded-lg p-4 bg-teal-50">
            <div className="flex justify-between items-center mb-3">
              <h4 className="font-semibold text-teal-800">
                Records Due Today ({preview.totalReminders} reminders for {preview.children.length} children)
              </h4>
              <Button variant="ghost" size="sm" onClick={() => setShowPreview(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            {preview.children.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No records due for reminders today based on your interval settings.</p>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {preview.children.map((child) => (
                  <div key={child.childId} className="bg-white p-3 rounded border">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">{child.childName}</p>
                        <p className="text-xs text-gray-500">
                          {child.parentEmail || "No email"} | {child.parentPhone || "No phone"}
                        </p>
                      </div>
                      <Badge variant="outline" className="bg-amber-50 text-amber-700">
                        {child.forms.length} due
                      </Badge>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {child.forms.map((form, idx) => (
                        <span key={idx} className="text-xs bg-gray-100 px-2 py-1 rounded">
                          {form.formName} ({form.daysUntilDue}d)
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* History Modal */}
        {showHistory && (
          <div className="mt-4 border border-gray-200 rounded-lg p-4 bg-gray-50">
            <div className="flex justify-between items-center mb-3">
              <h4 className="font-semibold text-gray-800">Reminder History</h4>
              <Button variant="ghost" size="sm" onClick={() => setShowHistory(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            {history.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No reminders have been sent yet.</p>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {history.map((log) => (
                  <div key={log.id} className="bg-white p-3 rounded border flex justify-between items-center">
                    <div>
                      <p className="text-sm font-medium">
                        {new Date(log.sentAt).toLocaleDateString()} at {new Date(log.sentAt).toLocaleTimeString()}
                      </p>
                      <p className="text-xs text-gray-500">
                        {log.childrenCount} children notified
                      </p>
                    </div>
                    <div className="text-right">
                      <span className="text-green-600 text-sm font-medium">{log.totalSent} sent</span>
                      {log.totalFailed > 0 && (
                        <span className="text-red-600 text-sm ml-2">{log.totalFailed} failed</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>

      {/* Send Confirmation Dialog */}
      <AlertDialog open={showSendConfirm} onOpenChange={setShowSendConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send Record Reminders Now?</AlertDialogTitle>
            <AlertDialogDescription>
              {preview.children.length > 0 ? (
                <>
                  This will send <strong>{preview.totalReminders}</strong> reminder(s) to <strong>{preview.children.length}</strong> parent(s) 
                  via <strong>{settings.notificationType === 'both' ? 'Email and SMS' : settings.notificationType.toUpperCase()}</strong>.
                  <br /><br />
                  Make sure you have saved your settings before sending.
                </>
              ) : (
                "No records are due for reminders today based on your current interval settings."
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            {preview.children.length > 0 && (
              <AlertDialogAction onClick={sendRemindersNow} className="bg-purple-600 hover:bg-purple-700">
                Yes, Send Reminders
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default RecordRemindersSettings;
